import React from 'react';
import PropTypes from 'prop-types';
import * as UploadUtils from '../../../../utils/upload-utils';
import { FilePickerState } from '../file-picker/FilePickerState';
import FilePicker from '../file-picker/FilePicker';

// convert file to base64 format
function getBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });
}

class Base64FilePicker extends React.Component {
    static preventDefaults(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    constructor(props) {
        super(props);
        const { editfile, defaultpath } = this.props;

        this.state = {
            error: undefined, // for error message
            eventState: editfile ? FilePickerState.uploaded : FilePickerState.start,
            // 'start', 'drag', 'drop', 'uploading', 'uploaded', 'failed'
            hasFile: editfile, // is a file curently uploaded in the filepicker
            fileName: '', // name of uploaded file
            previewUrl: editfile ? defaultpath : '',
            logoUrlUnchanged: true, // set logoURL name as 'logoUnchanged' unless new logo uploaded
            base64url: '',
        };
        this.validate = UploadUtils.validate.bind(this);
        this.handleFileChanged = this.handleFileChanged.bind(this);
        this.handleRemoveFile = this.handleRemoveFile.bind(this);
        this.handleLoad = this.handleLoad.bind(this);
        this.upload = this.upload.bind(this);
    }

    /// Process a new file upload event
    handleFileChanged(file) {
        const { passerror } = this.props;
        let { error } = this.state;
        let nextState = FilePickerState.uploading;

        if (!file) {
            nextState = FilePickerState.failed; // sets nextState to 'failed'
            error = 'The file you have you selected is invalid.';
            this.setState(() => ({
                eventState: nextState,
                hasFile: false,
                error,
            }));
            passerror(error);
        } else {
            const fileName = file.name;
            const previewUrl = window.URL.createObjectURL(file);

            this.setState({
                eventState: nextState,
                hasFile: false,
                fileName,
                previewUrl,
            }, () => {
                this.upload(file);
            });
        }
    }

    handleRemoveFile() {
        this.setState({
            fileName: '',
            previewUrl: '',
            base64url: '',
            logoUrlUnchanged: false,
        }, () => {
            this.handleLoad();
        });
    }

    handleLoad() {
        const { passerror } = this.props;
        const { fileName } = this.state;

        // validate the file upload in the parent form.
        passerror('');

        if (fileName) {
            // if fileName is empty and not in test environment, it means removed file
            this.setState(() => ({
                hasFile: true,
                eventState: FilePickerState.uploaded,
            }));
        } else {
            // once remove file, set eventState='start' so filePicker show initial state
            this.setState(() => ({
                hasFile: true,
                eventState: FilePickerState.start,
            }));
        }
    }

    /// Starts moving the file to S3 temp bucket in the background
    /// When the form is submitted the server can get the file from 'props.bucketpath'
    upload(file) {
        const {
            maxsize, extensions, passerror,
        } = this.props;
        const message = this.validate(file, maxsize, ...extensions);

        if (message) {
            passerror(message);

            this.setState(() => ({
                hasFile: false,
                error: message,
                eventState: FilePickerState.failed,
            }));
        } else {
            getBase64(file).then(
                (data) => {
                    this.handleLoad();
                    this.setState(() => ({
                        logoUrlUnchanged: false,
                        base64url: data,
                    }));
                },
            );
        }
    }

    render() {
        const {
            hasFile, logoUrlUnchanged, eventState, base64url, previewUrl, fileName,
        } = this.state;
        const {
            name, optional, maxsize, round, preview, passerror, editfile,
        } = this.props;

        const backgroundimage = `url(${`${previewUrl}`})`;
        let filePickerInputValue = '';

        if (hasFile) {
            filePickerInputValue = base64url;
            // To distinguish remove file and just click save without modify file,
            // if remove svg file, return value 'removed file'
            if (base64url === '' && !logoUrlUnchanged) {
                filePickerInputValue = 'removed file';
            }
        }
        const filePickerDefaultSubtitle = `Max upload size: ${maxsize}KB`;

        return (
            <FilePicker
                handleFileChanged={this.handleFileChanged}
                name={name}
                optional={optional}
                hasFile={hasFile}
                logoUrlUnchanged={logoUrlUnchanged}
                eventState={eventState}
                maxsize={maxsize}
                round={round}
                preview={preview}
                fileName={fileName}
                handleRemoveFile={this.handleRemoveFile}
                filePickerInputValue={filePickerInputValue}
                backgroundimage={backgroundimage}
                passerror={passerror}
                editfile={editfile}
                filePickerDefaultSubtitle={filePickerDefaultSubtitle}
            />
        );
    }
}

Base64FilePicker.propTypes = {
    name: PropTypes.string.isRequired,
    maxsize: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    extensions: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
    ]),
    passerror: PropTypes.func,
    round: PropTypes.bool,
    preview: PropTypes.bool,
    editfile: PropTypes.bool,
    defaultpath: PropTypes.string,
    optional: PropTypes.bool,
};

Base64FilePicker.defaultProps = {
    passerror: () => { },
    maxsize: undefined,
    extensions: undefined,
    round: false,
    preview: false,
    editfile: false,
    defaultpath: null,
    optional: false,
};

export default Base64FilePicker;
